import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Users,
  Mail,
  Phone,
  Briefcase,
  Building,
  ChevronLeft,
  ChevronRight,
  Facebook,
  Info,
  Home,
} from "lucide-react";
import api from "../../utils/axios.config";
import { baseUrl } from "../../utils/url";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";

const placeholderImg =
  "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?auto=format&fit=facearea&w=400&h=400&q=80";

const Committee = () => {
  const [searchParams] = useSearchParams();
  const committeeType = searchParams.get("type") || "central";
  const [activeTab, setActiveTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api.get(
          `/member?committeeName=${committeeType}`
        );
        setMembers(response.data.data);
      } catch (error) {
        setError("সদস্য তালিকা আনতে সমস্যা হয়েছে।");
      }
      setLoading(false);
    };

    fetchMembers();
    setCurrentPage(0);
  }, [committeeType]);

  const filteredMembers = members.filter((member) =>
    activeTab === "advisors"
      ? member.organizationDesignation === "advisor"
      : member.organizationDesignation === "member"
  );

  const pageCount = Math.ceil(filteredMembers.length / pageSize);
  const startIndex = currentPage * pageSize;
  const currentMembers = filteredMembers.slice(
    startIndex,
    startIndex + pageSize
  );

  const goToPage = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < pageCount) {
      setCurrentPage(pageNumber);
    }
  };

  const openModal = (member) => {
    setSelectedMember(member);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const getCommitteeName = (type) => {
    switch (type) {
      case "central":
        return "কেন্দ্রীয় কমিটি";
      case "cox-bazar":
        return "কক্সবাজার জেলা কমিটি";
      case "noakhali-lakshmipur":
        return "নোয়াখালী-লক্ষ্মীপুর জেলা কমিটি";
      default:
        return "কমিটি";
    }
  };

  return (
    <div className="py-8 px-4 lg:px-8 min-h-screen bg-white">
      <h1 className="text-center font-extrabold text-3xl tracking-wide mb-6">
        <span className="inline-flex items-center gap-2 text-primary">
          <Users className="w-7 h-7" />
          {getCommitteeName(committeeType)}
        </span>
      </h1>

      {/* Member/Advisor Tabs */}
      <div className="flex justify-center gap-4 mb-6">
        <button
          onClick={() => setActiveTab("members")}
          className={`px-5 py-2 rounded-full font-semibold shadow transition-colors border ${
            activeTab === "members"
              ? "bg-primary text-white scale-105 shadow-lg"
              : "bg-white text-gray-600 hover:bg-gray-100"
          }`}
        >
          Executive Member
        </button>
        <button
          onClick={() => setActiveTab("advisors")}
          className={`px-5 py-2 rounded-full font-semibold shadow transition-colors border ${
            activeTab === "advisors"
              ? "bg-primary text-white scale-105 shadow-lg"
              : "bg-white text-gray-600 hover:bg-gray-100"
          }`}
        >
          Adviser/Patron
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[18rem]">
          <div className="animate-spin rounded-full border-4 border-primary border-t-transparent h-14 w-14"></div>
        </div>
      ) : error ? (
        <div className="text-red-600 text-center py-12 font-bold">{error}</div>
      ) : currentMembers.length === 0 ? (
        <div className="text-gray-400 text-center py-12 font-medium">
          {activeTab === "members"
            ? "No executive members found."
            : "No advisers found."}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Photo
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Designation
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Occupation
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentMembers.map((member) => (
                  <tr key={member._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={
                            member.imageUrl
                              ? `${baseUrl}${member.imageUrl}`
                              : placeholderImg
                          }
                          alt={member.name}
                          onError={(e) => (e.target.src = placeholderImg)}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {member.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {member.designation}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {member.occupation}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openModal(member)}
                        className="text-primary hover:underline flex items-center justify-end w-full"
                      >
                        <Info className="mr-1" size={16} />
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex flex-wrap justify-center mt-6 gap-1">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 0}
                className="w-9 h-9 flex items-center justify-center rounded-full border bg-white hover:bg-gray-100 transition disabled:opacity-35 disabled:cursor-not-allowed text-xl"
              >
                <ChevronLeft />
              </button>
              {Array.from({ length: pageCount }, (_, index) => (
                <button
                  key={index}
                  onClick={() => goToPage(index)}
                  className={`w-9 h-9 rounded-full border flex items-center justify-center text-lg transition font-bold ${
                    index === currentPage
                      ? "bg-primary text-white scale-105 shadow"
                      : "bg-white hover:bg-gray-100 text-primary"
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === pageCount - 1}
                className="w-9 h-9 flex items-center justify-center rounded-full border bg-white hover:bg-gray-100 transition disabled:opacity-35 disabled:cursor-not-allowed text-xl"
              >
                <ChevronRight />
              </button>
            </div>
          )}

          {/* Member Detail Modal */}
          <Modal
            open={modalOpen}
            onClose={closeModal}
            center
            classNames={{
              modal: "rounded-lg max-w-lg md:max-w-lg",
            }}
          >
            {selectedMember && (
              <div className="p-6">
                <div className="flex flex-col items-center mb-4">
                  <img
                    className="w-32 h-32 rounded-full object-cover border-4 border-primary shadow-lg"
                    src={
                      selectedMember.imageUrl
                        ? `${baseUrl}${selectedMember.imageUrl}`
                        : placeholderImg
                    }
                    alt={selectedMember.name}
                    onError={(e) => (e.target.src = placeholderImg)}
                  />
                  <h2 className="mt-4 text-xl font-bold text-gray-900 text-center">
                    {selectedMember.name}
                  </h2>
                  <p className="text-sm text-primary font-medium">
                    {selectedMember.designation}
                  </p>
                  <p className="text-sm text-gray-500">
                    {selectedMember.occupation}
                  </p>
                </div>

                <div className="space-y-3">

                  <div className="flex items-start">
                    <Phone className="flex-shrink-0 h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Mobile
                      </p>
                      <p className="text-sm text-gray-900">
                        {selectedMember.mobile}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Mail className="flex-shrink-0 h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="text-sm text-gray-900">
                        {selectedMember.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Home className="flex-shrink-0 h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Address
                      </p>
                      <p className="text-sm text-gray-900">
                        {selectedMember.address}
                      </p>
                    </div>
                  </div>

                  {selectedMember.facebookUrl && (
                    <div className="flex items-center justify-center pt-2">
                      <a
                        href={selectedMember.facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-primary hover:bg-blue-200"
                      >
                        <Facebook className="w-4 h-4 mr-1" />
                        Facebook Profile
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Modal>
        </>
      )}
    </div>
  );
};

export default Committee;
